import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

function OutcomePill({ outcome }) {
  if (outcome === 'survived') return <span className="badge-green">Survived</span>
  if (outcome === 'eliminated') return <span className="badge-red">Out</span>
  if (outcome === 'void') return <span className="badge-gray">Void</span>
  return <span className="badge-gray">Pending</span>
}

// A gameweek's picks become visible to everyone once it's locked, processed,
// or its deadline has passed -- mirrors the exact condition the RLS policy
// (picks_read_own_or_after_freeze, migration 013) enforces server-side.
function isPastDeadline(gw, now) {
  if (gw.status === 'locked' || gw.status === 'processed') return true
  return !!gw.deadline && now >= new Date(gw.deadline).getTime()
}

// Level 3 (round detail) content: the player's pick history for this round,
// used-vs-available teams while still alive, and everyone's picks by
// gameweek once visible. Originally the standalone "My Game" page; per
// MY_EVENTS_HUB.md it now lives under the round it belongs to instead,
// reachable from the event page (LMSEventPage) for any entry status --
// not just while still alive -- so a completed run's history stays visible.
export default function RoundHistoryPanel({ entry, round }) {
  const [loading, setLoading] = useState(true)
  const [myPicks, setMyPicks] = useState([])
  const [usedTeams, setUsedTeams] = useState(new Set())
  const [allTeams, setAllTeams] = useState([])
  const [grid, setGrid] = useState(null)

  useEffect(() => {
    async function load() {
      setLoading(true)

      const [{ data: gwData }, { data: pickData }, { data: usedData }, { data: standingsData }] = await Promise.all([
        supabase.from('lms_gameweeks').select('*').eq('round_id', round.id).order('gameweek_number'),
        supabase.from('lms_picks').select('*').eq('entry_id', entry.id),
        supabase.from('lms_used_teams').select('team_name').eq('entry_id', entry.id).eq('round_id', round.id),
        supabase.from('pl_standings').select('team_name, position').order('position')
      ])

      const gameweeks = gwData || []
      const pickByGwId = new Map((pickData || []).map(p => [p.gameweek_id, p]))
      setMyPicks(gameweeks.map(gw => ({ gw, pick: pickByGwId.get(gw.id) || null })))
      setUsedTeams(new Set((usedData || []).map(u => u.team_name)))
      setAllTeams(standingsData || [])

      const now = Date.now()
      const pastGws = gameweeks.filter(gw => isPastDeadline(gw, now))

      if (pastGws.length > 0) {
        const [{ data: allPicksData }, nameResults] = await Promise.all([
          supabase.from('lms_picks').select('*').in('gameweek_id', pastGws.map(g => g.id)),
          Promise.all(pastGws.map(gw => supabase.rpc('get_lms_gameweek_pick_names', { p_gameweek_id: gw.id })))
        ])

        const names = new Map()
        nameResults.forEach(r => (r.data || []).forEach(n => names.set(n.entry_id, n.display_name)))

        const picksByEntry = new Map()
        for (const p of allPicksData || []) {
          if (!picksByEntry.has(p.entry_id)) picksByEntry.set(p.entry_id, new Map())
          picksByEntry.get(p.entry_id).set(p.gameweek_id, p)
        }

        setGrid({ gameweeks: pastGws, names, picksByEntry })
      } else {
        setGrid({ gameweeks: [], names: new Map(), picksByEntry: new Map() })
      }

      setLoading(false)
    }
    load()
  }, [entry, round])

  if (loading) return <div className="card"><p className="text-muted">Loading round history...</p></div>

  const stillPlaying = entry.status === 'active' || entry.status === 'pending_payment'
  const available = allTeams.filter(t => !usedTeams.has(t.team_name))

  return (
    <div className="card">
      <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '10px' }}>
        Your picks
      </div>
      {myPicks.length === 0 ? (
        <p className="text-muted" style={{ fontSize: '13px', marginBottom: '20px' }}>No gameweeks yet.</p>
      ) : (
        <div className="card-flat" style={{ marginBottom: '20px' }}>
          <table>
            <thead>
              <tr><th>GW</th><th>Team</th><th></th><th>Outcome</th></tr>
            </thead>
            <tbody>
              {myPicks.map(({ gw, pick }) => (
                <tr key={gw.id}>
                  <td style={{ fontWeight: '600', fontSize: '13px' }}>{gw.gameweek_number}</td>
                  <td style={{ fontSize: '13px' }}>{pick?.team_picked || '—'}</td>
                  <td>{pick?.is_auto_pick && <span className="badge-gray">Auto</span>}</td>
                  <td><OutcomePill outcome={pick?.outcome} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {stillPlaying && allTeams.length > 0 && (
        <>
          <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '10px' }}>
            Your teams — used vs available
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {allTeams.map(t => {
              const used = usedTeams.has(t.team_name)
              return (
                <span
                  key={t.team_name}
                  className={used ? 'badge-gray' : 'badge-green'}
                  style={used ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
                >
                  {t.team_name}
                </span>
              )
            })}
          </div>
          <p className="text-muted" style={{ fontSize: '12px', marginBottom: '20px' }}>
            {available.length} of {allTeams.length} teams still available.
          </p>
        </>
      )}

      {grid && grid.gameweeks.length > 0 && (
        <>
          <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '10px' }}>
            Everyone's picks by gameweek
          </div>
          <div className="card-flat" style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Player</th>
                  {grid.gameweeks.map(gw => <th key={gw.id} style={{ textAlign: 'center' }}>GW{gw.gameweek_number}</th>)}
                </tr>
              </thead>
              <tbody>
                {[...grid.picksByEntry.keys()].map(entryId => {
                  const isMe = entryId === entry.id
                  return (
                    <tr key={entryId} style={{ background: isMe ? 'var(--blue-dim)' : 'transparent' }}>
                      <td style={{ fontSize: '13px', fontWeight: isMe ? '700' : '500' }}>
                        {grid.names.get(entryId) || 'Player'}{isMe ? ' (you)' : ''}
                      </td>
                      {grid.gameweeks.map(gw => {
                        const p = grid.picksByEntry.get(entryId)?.get(gw.id)
                        return (
                          <td key={gw.id} style={{ textAlign: 'center', fontSize: '12px' }}>
                            {p ? (
                              <>
                                {p.team_picked}
                                {p.is_auto_pick && <span style={{ marginLeft: '4px' }} className="badge-gray">A</span>}
                              </>
                            ) : '—'}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
